// src/phase4/entities/EnemySprite.ts
// Enemy entity class - handles visual rendering and animations

import Phaser from 'phaser';
import { EnemyInstance, EnemyType } from '../types/enemy';
import { ENEMY_CONFIGS } from '../data/enemy-configs';

export interface EnemySpriteConfig {
  id: string;
  type: EnemyType;
  x: number;
  y: number;
  maxHp: number;
  scene: Phaser.Scene;
}

export class EnemySprite extends Phaser.GameObjects.Container {
  private config: EnemySpriteConfig;
  private enemyInstance: EnemyInstance;

  // Global scale factor for all enemies (adjust to change enemy size globally)
  // Current value 0.6 makes enemies 60% of their original size
  private static GLOBAL_SCALE = 0.6;

  // Visual elements
  private bodySprite?: Phaser.GameObjects.Sprite;
  private healthBarBg!: Phaser.GameObjects.Graphics;
  private healthBarFill!: Phaser.GameObjects.Graphics;
  private stealthOverlay?: Phaser.GameObjects.Graphics;

  // State
  private currentHp: number;
  private isStealthed: boolean = false;
  private isHit: boolean = false;

  constructor(scene: Phaser.Scene, config: EnemySpriteConfig) {
    super(scene, config.x, config.y);
    this.config = config;
    this.currentHp = config.maxHp;
    this.enemyInstance = null as unknown as EnemyInstance;

    // Create enemy instance
    this.enemyInstance = {
      id: config.id,
      type: config.type,
      maxHp: config.maxHp,
      currentHp: config.maxHp,
      speed: 0.002,
      x: config.x,
      y: config.y,
      pathProgress: 0,
      isDead: false,
      isStealthed: false,
    };

    this.init();
  }

  private init(): void {
    // Create visual elements
    this.createBody();
    this.createHealthBar();
    this.createStealthOverlay();

    // Update position
    this.setPosition(this.config.x, this.config.y);
  }

  private createBody(): void {
    const enemyConfig = ENEMY_CONFIGS[this.config.type];
    const textureKey = enemyConfig?.textureKey || 'enemy_default';

    if (this.scene.textures.exists(textureKey)) {
      this.bodySprite = this.scene.add.sprite(0, 0, textureKey);
      const size = enemyConfig?.size || 24;
      this.bodySprite.setScale((size / 32) * EnemySprite.GLOBAL_SCALE); // Apply global scale
      this.bodySprite.setOrigin(0.5, 0.5);
      this.add(this.bodySprite);
    } else {
      // Fallback to graphics with emoji-style representation
      const graphics = this.scene.add.graphics();
      const color = this.getBodyColor();
      const size = 24 * EnemySprite.GLOBAL_SCALE;

      // Main body
      graphics.fillStyle(color, 1);
      graphics.fillCircle(0, 0, size / 2);

      // Border
      graphics.lineStyle(2, 0xffffff, 0.8);
      graphics.strokeCircle(0, 0, size / 2);

      // Add icon based on type
      const icon = this.getTypeIcon();
      const text = this.scene.add.text(0, 0, icon, { fontSize: `${16 * EnemySprite.GLOBAL_SCALE}px` });
      text.setOrigin(0.5, 0.5);
      this.add(text);

      this.add(graphics);
    }
  }

  private createHealthBar(): void {
    const barWidth = 40;
    const barHeight = 6;
    const barOffset = -20;

    // Background
    this.healthBarBg = this.scene.add.graphics();
    this.healthBarBg.fillStyle(0x3a362e, 1);
    this.healthBarBg.fillRoundedRect(-barWidth / 2, barOffset, barWidth, barHeight, 2);
    this.add(this.healthBarBg);

    // Fill
    this.healthBarFill = this.scene.add.graphics();
    this.updateHealthBar();
    this.add(this.healthBarFill);
  }

  private createStealthOverlay(): void {
    this.stealthOverlay = this.scene.add.graphics();
    this.stealthOverlay.fillStyle(0x5cd6a8, 0.2);
    const size = 24;
    this.stealthOverlay.fillCircle(0, 0, size / 2);
    this.stealthOverlay.lineStyle(1, 0x5cd6a8, 1);
    this.stealthOverlay.strokeCircle(0, 0, size / 2);
    this.stealthOverlay.setVisible(false);
    this.add(this.stealthOverlay);
  }

  private getBodyColor(): number {
    switch (this.config.type) {
      case EnemyType.SQL_INJECTION:
        return 0xff6b6b; // Red
      case EnemyType.XSS_ATTACK:
        return 0xffd93d; // Yellow
      case EnemyType.DDOS_BOT:
        return 0x6bcb77; // Green
      case EnemyType.TROJAN:
        return 0x9b59b6; // Purple
      case EnemyType.ZERO_DAY:
        return 0xe74c3c; // Dark red
      default:
        return 0x95a5a6;
    }
  }

  private getTypeIcon(): string {
    switch (this.config.type) {
      case EnemyType.SQL_INJECTION:
        return '💉';
      case EnemyType.XSS_ATTACK:
        return '⚠️';
      case EnemyType.DDOS_BOT:
        return '🧟';
      case EnemyType.TROJAN:
        return '🐴';
      case EnemyType.ZERO_DAY:
        return '💀';
      default:
        return '🐛';
    }
  }

  // Health updates
  updateHealth(amount: number): void {
    this.currentHp = Math.max(0, amount);
    this.updateHealthBar();
  }

  private updateHealthBar(): void {
    const barWidth = 40;
    const barHeight = 6;
    const barOffset = -20;

    const hpPercent = this.currentHp / this.config.maxHp;
    const fillWidth = Math.max(0, barWidth * hpPercent);

    this.healthBarFill.clear();
    this.healthBarFill.fillStyle(0x5cd6a8, 1);
    this.healthBarFill.fillRoundedRect(-barWidth / 2, barOffset, fillWidth, barHeight, 2);
  }

  // Stealth handling
  setStealthed(stealthed: boolean): void {
    this.isStealthed = stealthed;
    if (this.stealthOverlay) {
      this.stealthOverlay.setVisible(stealthed);
    }
    if (this.bodySprite) {
      this.bodySprite.setAlpha(stealthed ? 0.4 : 1);
    }
  }

  // Hit effect
  playHitAnimation(): void {
    if (this.isHit) return;
    this.isHit = true;

    // Flash red
    this.scene.tweens.add({
      targets: this,
      alpha: 0.5,
      duration: 50,
      yoyo: true,
      onComplete: () => {
        this.isHit = false;
      },
    });

    // Shake
    this.scene.tweens.add({
      targets: this,
      x: this.x + 3,
      duration: 30,
      yoyo: true,
      repeat: 2,
    });

    // Break stealth
    if (this.isStealthed) {
      this.setStealthed(false);
    }
  }

  // Death animation
  async playDeathAnimation(): Promise<void> {
    // Fade out
    return new Promise((resolve) => {
      this.scene.tweens.add({
        targets: this,
        alpha: 0,
        scale: 1.5,
        duration: 200,
        ease: 'Quad.easeOut',
        onComplete: () => {
          this.destroy();
          resolve();
        },
      });
    });
  }

  // Position update (called by EnemySystem)
  updatePosition(x: number, y: number): void {
    this.setPosition(x, y);
  }

  // Getters
  getId(): string {
    return this.config.id;
  }

  getType(): EnemyType {
    return this.config.type;
  }

  getHp(): number {
    return this.currentHp;
  }

  getMaxHp(): number {
    return this.config.maxHp;
  }

  getInstance(): EnemyInstance {
    return {
      ...this.enemyInstance,
      currentHp: this.currentHp,
      isStealthed: this.isStealthed,
    };
  }
}

// Factory function
export function createEnemySprite(
  scene: Phaser.Scene,
  type: EnemyType,
  x: number,
  y: number,
  maxHp: number,
  id: string
): EnemySprite {
  return new EnemySprite(scene, {
    id,
    type,
    x,
    y,
    maxHp,
    scene,
  });
}
